import { FC, useContext, useEffect, useState, useCallback, useMemo } from "react";
import "./PersonalSettings.css";
import CrossImg from "../../../assets/images/cross.svg";
import { Context } from "../../..";
import { AppContext } from "../../../App";
import Avatar, { AvatarSize } from "../../Shared/Avatar/Avatar";
import ImageSelector from "../../Shared/ImageSelector/ImageSelector";
import { InputSwitch } from "primereact/inputswitch";
import {
    NotificationSettingsMap,
    ProjectEmailNotificationSettingsMap,
    TaskEmailNotificationSettingsMap,
} from "./NotificationSettingsMap";
import Loader from "../../Shared/Loader/Loader";
import { CurrentTab, TabNavigator } from "../../UiLib/TabNavigator/TabNavigator";
import { isAxiosError } from "axios";
import DeleteElementGray from "../../../assets/deleteElementGreyTrue.svg";
import СircleArrow from "../../../assets/circleArrow.svg";
import { IToken } from "../../../common/api_interfaces/token/IToken";
import UserService from "../../../services/UserService";
import ConfirmationWindow, { IConfirmationWindow } from "../../Shared/ConfirmationWindow/ConfirmationWindow";
import DocumentCopy from "../../../assets/document-copy.svg";
import { useUserContext } from "../../../contexts/UserContext";
import { hasPermission } from "../../../helpers/hasPermisson";
import InputField from "../../UiLib/InputField/InputField";
import { eventService } from "../../../events/EventServise";
import { IEditPersonalSettings, IEditPersonalSettingsRequest } from "../../../common/api_interfaces/user/IEditPersonalSettings";
import { IEditUser } from "../../../common/api_interfaces/user/IEditUser";
import { personalSettings } from "../../../common/enums/personalSettings";
import FileService from "../../../services/FileService";

interface ICurrentSettings {
    showProxyBoards: IEditPersonalSettings;
    showSubProjects: IEditPersonalSettings;
    saveBoardFilters: IEditPersonalSettings;
    notificationSettings: IEditPersonalSettings[];
}

interface IPersonalSettingsProps {
    onClose: () => void;
}

const PersonalSettings: FC<IPersonalSettingsProps> = ({ onClose }) => {
    const { showToast } = useContext(AppContext);
    const { store } = useContext(Context);
    const { systemUserAccess } = useUserContext();

    const [firstName, setFirstName] = useState<string>(store.user.name || "");
    const [surname, setSurname] = useState<string>(store.user.surname || "");
    const [middleName, setMiddleName] = useState<string>(store.user.middlename || "");
    const [city, setCity] = useState<string>(store.user.city || "");
    const [nameError, setNameError] = useState<string>("");
    const [surnameError, setSurnameError] = useState<string>("");
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [newPhoto, setNewPhoto] = useState<File>();
    const [modalWindowData, setModalWindowData] = useState<IConfirmationWindow>();
    const [tokenData, setTokenData] = useState<IToken>();
    const [copyBtnActive, setCopyBtnActive] = useState<boolean>(false);

    const defaultSettings = useMemo((): ICurrentSettings => ({
        showProxyBoards: {
            id: undefined,
            paramName: personalSettings.showProxyBoardsLeftMenu,
            value: true,
        },
        showSubProjects: {
            id: undefined,
            paramName: personalSettings.showSubprojectsProjectPage,
            value: true,
        },
        saveBoardFilters: {
            id: undefined,
            paramName: personalSettings.saveBoardFilters,
            value: true,
        },
        notificationSettings: Array.from(NotificationSettingsMap).map(([paramName]) => ({
            id: undefined,
            paramName,
            value: true,
        })),
    }), []);

    const [currentSettings, setCurrentSettings] = useState<ICurrentSettings>(defaultSettings);

    useEffect(() => {
        const initializeData = async () => {
            await store.getPersonalSettings();
        };
        initializeData();
    }, [store]);

    useEffect(() => {
        if (store.personalSettings.length === 0) return;

        const updatedSettings: ICurrentSettings = {
            showProxyBoards: store.personalSettings.find(s => s.paramName === personalSettings.showProxyBoardsLeftMenu) 
                || defaultSettings.showProxyBoards,
            showSubProjects: store.personalSettings.find(s => s.paramName === personalSettings.showSubprojectsProjectPage) 
                || defaultSettings.showSubProjects,
            saveBoardFilters: store.personalSettings.find(s => s.paramName === personalSettings.saveBoardFilters) 
                || defaultSettings.saveBoardFilters,
            notificationSettings: defaultSettings.notificationSettings.map(setting => 
                store.personalSettings.find(s => s.paramName === setting.paramName) || setting
            ),
        };

        setCurrentSettings(updatedSettings);
    }, [store.personalSettings, defaultSettings]);

    const validateName = useCallback((): boolean => {
        const trimmedName = firstName.trim();
        const error = !trimmedName ? "Заполните имя" : "";
        setNameError(error);
        return !!error;
    }, [firstName]);

    const validateSurname = useCallback((): boolean => {
        const trimmedSurname = surname.trim();
        const error = !trimmedSurname ? "Заполните фамилию" : "";
        setSurnameError(error);
        return !!error;
    }, [surname]);

    const changeParam = useCallback((paramName: string) => {
        setCurrentSettings(prev => {
            const updateSetting = (setting: IEditPersonalSettings) => 
                setting.paramName === paramName ? { ...setting, value: !setting.value } : setting;

            return {
                ...prev,
                showProxyBoards: updateSetting(prev.showProxyBoards),
                showSubProjects: updateSetting(prev.showSubProjects),
                saveBoardFilters: updateSetting(prev.saveBoardFilters),
                notificationSettings: prev.notificationSettings.map(updateSetting),
            };
        });
    }, []);

    const saveSettings = useCallback(async () => {
        if (validateName() || validateSurname()) {
            setShowLoader(false);
            return;
        }

        setShowLoader(true);
        try {
            let photoId = store.user.photoId ?? null;

            if (newPhoto) {
                const response = await store.fileService.uploadTempFile(newPhoto);
                photoId = response.data.id;
            }

            const editUserReq: IEditUser = {
                name: firstName.trim(),
                surname: surname.trim(),
                middlename: middleName.trim() || null,
                city: city.trim() || null,
                photoId,
            };

            const editSettingsReq: IEditPersonalSettingsRequest = {
                settings: [
                    currentSettings.showProxyBoards,
                    currentSettings.showSubProjects,
                    currentSettings.saveBoardFilters,
                    ...currentSettings.notificationSettings,
                ],
            };

            await store.editPersonalSettings(editSettingsReq);
            const user = await store.userService.editPersonalInfo(editUserReq);

            showToast("Сохранено");

            if (newPhoto) {
                store.fileService.refreshUserAvatar(user.data.id);

                let userId = user.data.id;
                eventService.emit('userPhotoUpdated', { userId });
            }
            
            store.setUser(user.data);
        } catch (err) {
            showToast(`Ошибка при сохранении: ${err}`);
        } finally {
            setShowLoader(false);
        }
    }, [firstName, surname, middleName, city, store.user.photoId, newPhoto, currentSettings, store, validateName, validateSurname, showToast]);

    const getTokenData = useCallback(async () => {
        try {
            const token = await UserService.getToken();
            setTokenData(token.data);
        } catch (err) {
            if (isAxiosError(err) && err.status !== 404) {
                showToast(err.response?.data.message);
            }
        }
    }, [showToast]);

    const generateToken = useCallback(async () => {
        try {
            setModalWindowData(undefined);
            const token = await UserService.generateToken({ tokenId: tokenData?.id });
            setTokenData(token.data);
            setCopyBtnActive(true);
            showToast("Токен выслан вам на почту");
        } catch (err) {
            if (isAxiosError(err)) showToast(err.response?.data.message);
        }
    }, [tokenData, showToast]);

    const deleteToken = useCallback(async () => {
        try {
            await UserService.deleteToken({ tokenId: tokenData?.id });
            setTokenData(undefined);
            setCopyBtnActive(false);
            showToast("Токен удалён");
            setModalWindowData(undefined);
        } catch (err) {
            if (isAxiosError(err)) showToast(err.response?.data.message);
        }
    }, [tokenData, showToast]);

    const onCopy = useCallback(() => {
        if (!tokenData?.token) return;
        navigator.clipboard.writeText(tokenData.token);
        showToast("Скопировано в буфер обмена");
    }, [tokenData, showToast]);

    const PersonalTab = useMemo(() => () => (
        <div className="settings-body-container">
            {showLoader && <Loader />}
            <div className="personal-settings-avatar-container">
                <Avatar
                    srcBase64={newPhoto ? URL.createObjectURL(newPhoto) : undefined}
                    userId={store.user.id}
                    size={AvatarSize.xxl}
                />
                <div className="personal-settings-image-selector">
                    <ImageSelector
                        onImageCropped={setNewPhoto}
                        fileName={`${surname}_avatar.png`}
                    />
                </div>
            </div>
            <InputField
                label="Фамилия*"
                id="input_surname"
                value={surname}
                placeholder="Фамилия"
                error={surnameError}
                onBlur={validateSurname}
                onChange={setSurname}
            />
            <InputField
                label="Имя*"
                id="input_name"
                value={firstName}
                placeholder="Имя"
                error={nameError}
                onBlur={validateName}
                onChange={setFirstName}
            />
            <InputField
                label="Отчество"
                id="input_middlename"
                value={middleName}
                placeholder="Отчество"
                onChange={setMiddleName}
            />
            <InputField
                label="Город"
                id="input_city"
                value={city}
                placeholder="Город"
                onChange={setCity}
            />
        </div>
    ), [showLoader, newPhoto, surname, firstName, middleName, city, nameError, surnameError, validateName, validateSurname, store.user.id]);

    const SettingsSwitch = useCallback((label: string, show: boolean, onChange: () => void) => (
        <div className="additional-settings-switch-container">
            <label className="input_label">{label}</label>
            <InputSwitch className={show ? "show-completed-input-switch" : ""} checked={show} onChange={onChange} />
        </div>
    ), []);

    const NotificationPanel = useMemo(() => () => (
        <div>
            <h1 className="personal-settings-divider-text">Уведомления на почту о задачах</h1>
            {currentSettings.notificationSettings.map(setting => {
                const label = TaskEmailNotificationSettingsMap.get(setting.paramName);
                return label ? SettingsSwitch(label, setting.value, () => changeParam(setting.paramName)) : null;
            })}
            <h1 className="personal-settings-divider-text">Уведомления на почту о проектах</h1>
            {currentSettings.notificationSettings.map(setting => {
                const label = ProjectEmailNotificationSettingsMap.get(setting.paramName);
                return label ? SettingsSwitch(label, setting.value, () => changeParam(setting.paramName)) : null;
            })}
        </div>
    ), [currentSettings.notificationSettings, SettingsSwitch, changeParam]);

    const AdditionalTab = useMemo(() => () => (
        <div className="settings-body-container">
            {SettingsSwitch('Отображать прокси-доски в меню "Проекты"', currentSettings.showProxyBoards.value, 
                () => changeParam(currentSettings.showProxyBoards.paramName)
            )}
            {SettingsSwitch('Отображать подпроекты на странице "Проекты"', currentSettings.showSubProjects.value, 
                () => changeParam(currentSettings.showSubProjects.paramName)
            )}
            {SettingsSwitch("Сохранять состояние фильтров на канбан-досках", currentSettings.saveBoardFilters.value, 
                () => changeParam(currentSettings.saveBoardFilters.paramName)
            )}
            <NotificationPanel />
        </div>
    ), [currentSettings, SettingsSwitch, changeParam, NotificationPanel]);

    const SecurityTab = useMemo(() => () => (
        <div className="settings-body-container">
            <h1 className="personal-settings-divider-text">Токен</h1>
            <div className="token-container">
                <label className="token-text">{tokenData?.token || "Сгенерируйте токен"}</label>
                <div>
                    {copyBtnActive && <img src={DocumentCopy} onClick={onCopy} alt="Копировать" />}
                    {hasPermission(systemUserAccess, "TokenAction", "create") && (
                        <img
                            src={СircleArrow}
                            onClick={() => setModalWindowData({
                                bodyText: tokenData?.token 
                                    ? "Ваш старый токен будет удалён и создан новый, вы уверены?"
                                    : "Вы уверены, что хотите создать токен?",
                                headerText: tokenData?.token ? "Поменять токен?" : "Создать токен?",
                                cancelText: "Нет",
                                confirmText: "Да",
                                onCancel: () => setModalWindowData(undefined),
                                onConfirm: generateToken,
                            })}
                            alt="Сгенерировать токен"
                        />
                    )}
                    {tokenData?.token && hasPermission(systemUserAccess, "TokenAction", "delete") && (
                        <img
                            src={DeleteElementGray}
                            onClick={() => setModalWindowData({
                                bodyText: "Ваш старый токен будет удалён, вы уверены?",
                                headerText: "Удалить токен?",
                                cancelText: "Нет",
                                confirmText: "Да",
                                onCancel: () => setModalWindowData(undefined),
                                onConfirm: deleteToken,
                            })}
                            alt="Удалить токен"
                        />
                    )}
                </div>
                {modalWindowData && (
                    <ConfirmationWindow {...modalWindowData} />
                )}
            </div>
        </div>
    ), [tokenData, copyBtnActive, systemUserAccess, onCopy, generateToken, deleteToken, modalWindowData]);

    return (
        <div className="personal-settings-container-container">
            <div className="personal-settings-container">
                <div className="personal-settings-header">
                    <h1 className="header-text">Настройки</h1>
                    <button className="button-base personal-settings-close-button" onClick={onClose}>
                        <img src={CrossImg} alt="Закрыть" />
                    </button>
                </div>
                <TabNavigator>
                    <CurrentTab name="Персональные">{PersonalTab()}</CurrentTab>
                    <CurrentTab name="Дополнительно">{AdditionalTab()}</CurrentTab>
                    {hasPermission(systemUserAccess, "TokenAction", "view") && (
                        <CurrentTab name="Безопасность" callBack={getTokenData}>
                            {SecurityTab()}
                        </CurrentTab>
                    )}
                </TabNavigator>
                <div className="modal-window-btns">
                    <button
                        data-testid="button-personal_settings-save"
                        className="p_white p_14 button_reset_style save_btn"
                        onClick={saveSettings}
                    >
                        Сохранить
                    </button>
                    <button
                        data-testid="button-personal_settings-cancel"
                        className="modal-window-margin-btn confirm_btn p_blue p_14 button_reset_style"
                        onClick={onClose}
                    >
                        Отмена
                    </button>
                </div>
            </div>
        </div>
    );
};



export default PersonalSettings;